import React, { useEffect, useMemo, useState } from 'react';
import api from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import InternalSidebar from '../../components/InternalSidebar';
import InternalTopMenu from '../../components/InternalTopMenu';
import EmailVerificationBanner from '../../components/EmailVerificationBanner';
import BalanceVisibilityToggle from '../../components/BalanceVisibilityToggle';
import MaskedValue from '../../components/MaskedValue';

const Accounts = () => {
  const { user, logout } = useAuth();
  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!user?.id) return;

    const fetchData = async () => {
      try {
        setLoading(true);
        setError('');
        const response = await api.get(`/dashboard/overview/${user.id}`);
        setOverview(response.data);
      } catch (err) {
        setError(
          err.response?.data?.message ||
            err.message ||
            'Não foi possível carregar as carteiras.'
        );
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [user?.id]);

  const balances = useMemo(() => {
    const raw = overview?.balances?.raw || {};
    const formatted = overview?.balances?.formatted || {};
    const percents = overview?.balances?.percentages || {};

    const formatPercent = (value) => {
      const numeric = Number(value ?? 0);
      if (!Number.isFinite(numeric)) return '0%';
      return `${numeric.toFixed(2)}%`;
    };

    return {
      total: {
        value: formatted?.total || '0,00',
      },
      real: {
        value: formatted?.real || '0,00',
        distribution: formatted?.realDistribution || '0,00',
        percent: formatPercent(percents.real),
      },
      bitcoin: {
        value: formatted?.bitcoinAmount || '0,00000000',
        fiat: formatted?.bitcoinReal || '0,00',
        distribution: formatted?.bitcoinDistribution || '0,00',
        percent: formatPercent(percents.bitcoin),
      },
      raw,
    };
  }, [overview]);

  const [sidebarOpen, setSidebarOpen] = useState(false);
  const toggleSidebar = () => setSidebarOpen((prev) => !prev);

  return (
    <div className="min-h-screen bg-[#f7f7f7] text-slate-700">
      <div className="min-h-screen flex flex-col lg:flex-row">
        <div className="lg:hidden">
          <div className="flex items-center justify-between px-6 py-4 bg-white shadow-sm">
            <button
              type="button"
              className="text-2xl text-slate-700"
              onClick={toggleSidebar}
              aria-label="Abrir menu"
            >
              <i className="bi-list"></i>
            </button>
            <img src="/img/branded/logob.png" alt="Stackfy" className="h-8" />
            <div className="w-7" />
          </div>
        </div>

        <InternalSidebar active="accounts" open={sidebarOpen} onClose={() => setSidebarOpen(false)} />

        {sidebarOpen && (
          <button
            type="button"
            className="fixed inset-0 bg-slate-900/50 z-40 lg:hidden"
            onClick={() => setSidebarOpen(false)}
            aria-label="Fechar menu"
          ></button>
        )}

        <main className="flex-1 px-6 py-8 lg:px-10 lg:py-12">
          <EmailVerificationBanner />
          <header className="mb-8 flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
            <div>
              <h1 className="text-2xl font-semibold text-slate-900">Carteiras</h1>
              <p className="text-sm text-slate-500">Resumo consolidado dos seus saldos</p>
            </div>

            <div className="flex items-center gap-3">
              <BalanceVisibilityToggle />
              <InternalTopMenu onLogout={logout} />
            </div>
          </header>

          {loading && <div className="text-sm text-gray-500">Carregando carteiras...</div>}

          {error ? (
            <div className="mb-4 rounded border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-600">{error}</div>
          ) : (
            overview && (
              <>
                <section className="grid gap-6 xl:grid-cols-2 mb-10">
                  <div className="rounded-2xl border border-slate-100 bg-white px-6 py-6 shadow-sm flex flex-col gap-4">
                    <div className="flex items-center justify-between gap-4">
                      <div className="text-sm text-gray-400">Preço atual</div>
                      <a
                        href="/transact"
                        className="inline-flex items-center gap-2 rounded-full bg-tBlue text-white px-4 py-2 text-xs font-semibold hover:bg-tBlue2 transition-colors"
                      >
                        Comprar Bitcoin <i className="bi-arrow-right-short text-lg"></i>
                      </a>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="flex items-center text-sm text-gray-500 gap-2">
                        <i className="bi-currency-bitcoin bg-[#F79319] text-white rounded-full px-[5px] py-[2px]" />
                        1 BTC =
                      </div>
                      <div className="text-3xl font-semibold text-slate-900">
                        {overview?.bitcoinPrice?.formatted || '—'}
                      </div>
                    </div>
                  </div>

                  <div className="rounded-2xl border border-slate-100 bg-white px-6 py-6 shadow-sm flex flex-col gap-4">
                    <div className="flex items-center justify-between text-sm text-gray-400">
                      <span>Distribuição</span>
                      <span>100%</span>
                    </div>
                    <div className="flex h-2 rounded-full bg-slate-200 overflow-hidden">
                      <div
                        className="h-full bg-green-500 transition-all"
                        style={{ width: balances.real.percent }}
                      ></div>
                      <div
                        className="h-full bg-[#F7931A] transition-all"
                        style={{ width: balances.bitcoin.percent }}
                      ></div>
                    </div>
                    <div className="flex flex-col gap-2 text-xs text-slate-500">
                      <div className="flex items-center gap-2">
                        <i className="bi-circle-fill text-[8px] text-green-500"></i>
                        <span>Reais: <MaskedValue>{balances.real.distribution}</MaskedValue> ({balances.real.percent})</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <i className="bi-circle-fill text-[8px] text-[#F7931A]"></i>
                        <span>Bitcoin: <MaskedValue>{balances.bitcoin.fiat}</MaskedValue> ({balances.bitcoin.percent})</span>
                      </div>
                    </div>
                  </div>
                </section>

                <section className="mb-12">
                  <div className="rounded-3xl border border-slate-200 bg-white overflow-hidden shadow-sm">
                    <div className="flex items-center justify-between px-6 py-5">
                      <div className="flex items-center gap-3 text-sm text-slate-600">
                        <i className="bi-graph-up text-lg text-slate-500"></i>
                        <div>
                          <h2 className="text-base font-semibold text-slate-900">Saldo consolidado</h2>
                          <p className="text-xs text-slate-500">Soma de todos os ativos Stackfy</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 text-2xl font-semibold text-slate-900">
                        <span className="text-sm font-normal text-gray-500">R$</span>
                        <span><MaskedValue>{balances.total.value}</MaskedValue></span>
                      </div>
                    </div>

                    <div className="divide-y divide-slate-100">
                      <div className="flex items-center justify-between px-6 py-5">
                        <div className="flex items-center gap-3 text-slate-700">
                          <img
                            src="https://static.vecteezy.com/system/resources/previews/011/419/619/original/brazil-flag-file-free-png.png"
                            alt="Real"
                            className="w-5 h-4 rounded"
                          />
                          <div>
                            <h3 className="text-sm font-semibold">Real</h3>
                            <p className="text-xs text-slate-500">
                              Distribuição: <MaskedValue>{balances.real.distribution}</MaskedValue> ({balances.real.percent})
                            </p>
                          </div>
                        </div>
                        <div className="flex items-baseline gap-2 text-slate-900">
                          <span className="text-sm text-gray-500">R$</span>
                          <span className="text-xl font-semibold"><MaskedValue>{balances.real.value}</MaskedValue></span>
                        </div>
                      </div>

                      <div className="flex items-center justify-between px-6 py-5">
                        <div className="flex items-center gap-3 text-slate-700">
                          <i className="bi-currency-bitcoin bg-[#F79319] text-white rounded-full px-[6px] py-[3px] text-lg"></i>
                          <div>
                            <h3 className="text-sm font-semibold">Bitcoin</h3>
                            <p className="text-xs text-slate-500">
                              Distribuição: <MaskedValue>{balances.bitcoin.distribution}</MaskedValue> ({balances.bitcoin.percent})
                            </p>
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="flex items-baseline gap-2 text-slate-900 justify-end">
                            <span className="text-xl font-semibold"><MaskedValue>{balances.bitcoin.value}</MaskedValue></span>
                            <span className="text-xs text-gray-500">BTC</span>
                          </div>
                          <div className="text-xs text-slate-500 mt-1">~ <MaskedValue>{balances.bitcoin.fiat}</MaskedValue> em Reais</div>
                        </div>
                      </div>
                    </div>
                  </div>
                </section>

                <section className="mb-12">
                  <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-4">
                    <h3 className="text-base font-semibold text-slate-900 tracking-wide">Atalhos</h3>
                    <a
                      href="/transactions"
                      className="text-sm font-semibold text-slate-700 flex items-center gap-1 hover:text-slate-900"
                    >
                      Ver extrato <i className="bi-arrow-right-short text-tOrange text-2xl"></i>
                    </a>
                  </div>
                  <hr className="my-4" />
                  <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-4">
                    </div>  
                  <div className="grid gap-4 md:grid-cols-3">
                    {(overview?.shortcuts || []).map((shortcut) => (
                      <a
                        href={shortcut.href}
                        key={shortcut.id}
                        className="rounded-2xl border border-transparent bg-gradient-to-br from-[#EEF6FF] via-[#E3EDFF] to-[#D7E5FF] px-5 py-4 shadow-sm transition-all hover:-translate-y-1 hover:shadow-lg focus:outline-none focus:ring-2 focus:ring-[#0255F1]/40 flex items-center gap-4"
                      >
                        <div className="flex h-10 w-10 items-center justify-center rounded-full bg-white text-[#0255F1] text-xl shadow-inner">
                          <img src={shortcut.icon} alt={shortcut.label} className="w-6 h-6 object-contain" />
                        </div>
                        <div>
                          <div className="text-sm font-semibold text-[#012B87]">{shortcut.label}</div>
                          <div className="text-xs text-slate-500 mt-1">Acesse esta ação rapidamente</div>
                        </div>
                      </a>
                    ))}
                  </div>
                </section>
              </>
            )
          )}
        </main>
      </div>
    </div>
  );
};

export default Accounts;

